import { TableContainer } from "@/components/table-container";
import { useDynamicForm } from "@/components/dynamic-form/hook/useDynamicForm";
import { enhanceEndNoDayTime } from "@/components/dynamic-form/formatter/enhanceEndTime";
import { getInitialValues } from "@/components/dynamic-form/helper/getInitialValues";
import { DynamicForm } from "@/components/dynamic-form";
import { useWatchPagination } from "@/components/dynamic-form/hook/useWatchPagination";
import { Table, Drawer, Button } from "@renderbus/ui";
import { getSearchConfig } from "@/pages/consumption/view/billing/config/search-config";
import { useTableColumns } from "@/pages/consumption/view/billing/hooks/useTableColumns";
import { getOperationType } from "@/components/operation-button/helper/getOperationType";
import { Operation } from "@/components/operation-button";
import { IExportData } from "@/pages/export-list/types";
import { downloadFile } from "project-config/utils";
import { Modal } from "@/components/renderbus-modal";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import request from "project-config/services/request/request";
import { useAppealBill } from "@/pages/consumption/view/billing/hooks/useAppealBill";
import styled, { createGlobalStyle } from "styled-components";
import { AppealForm } from "@/pages/consumption/view/billing/components/appeal-form";
import { AppealProgressForm } from "@/pages/consumption/view/billing/components/appeal-progress";
import { AppealStatus, ConfirmStatus } from "./constant";
import { message } from "antd";
import { useConsumeStore } from "../../store/ConsumeStore";
import { BillTabTypeEnum } from "../../constants";
import queryString from "query-string";
import { downloadRequest } from "@/utils/downloadRequest";

const searchConfig = getSearchConfig();

export function RenderBilling() {
  const { activeKey } = useConsumeStore();
  const searchParams = queryString.parse(window.location.search);
  const type = searchParams.type as string;
  const { form, tableData, fetchData, pageNum, setPageNum, total, pageSize } =
    useDynamicForm<IExportData>({
      api: "/api/rendering/user/consume/bill/getUserMonthBills",
      defaultPageSize: 10,
      formatter: {
        time: enhanceEndNoDayTime,
        company: (v) => v || undefined,
      },
    });

  const fetch = async () => {
    setFilterValue(null);
    await fetchData();
  };

  useWatchPagination(pageSize, pageNum, fetch);

  const { columns, filterValue, setFilterValue } = useTableColumns();

  const { t } = useTranslation("center");

  const [openConfirmModal, setConfirmModal] = useState<number>();
  const [appealDrawer, setAppealDrawer] = useState(false);

  const {
    appealForm,
    setBillInfo,
    billInfo,
    onOk,
    isShowProgressForm,
    setIsShowProgressForm,
    showComplaintProgressDialog,
    appealProgressList,
    fetchAppealData,
    currentAppealStatus,
    openRevokeModal,
    setRevokeModal,
  } = useAppealBill();

  const [isSubmitAppealForm, setIsSubmitAppealForm] = useState(false);

  const closeAppealForm = async () => {
    setAppealDrawer(false);
    await fetch();
    setBillInfo(undefined);
    appealForm.resetFields();
    setIsSubmitAppealForm(false);
    setIsShowProgressForm(false);
  };
  const handleOperateAppeal = () => {
    if (billInfo?.appealStatus !== null || isSubmitAppealForm) {
      fetchAppealData(billInfo?.billNo).then((res) => {
        showComplaintProgressDialog(res, true);
      });
      setIsSubmitAppealForm(isSubmitAppealForm);
      return;
    }
    onOk().then(() => setIsSubmitAppealForm(true));
  };

  useEffect(() => {
    if (activeKey === BillTabTypeEnum.Billing) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  useEffect(() => {
    if (type === BillTabTypeEnum.Billing) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  return (
    <TableContainer noGap>
      <DynamicForm
        initialValues={getInitialValues(searchConfig)}
        searchConfig={searchConfig}
        searchForm={form}
        onSearch={() => fetch()}
        showButtonIcon
      />
      <Table<IExportData>
        id="billing-record-table"
        paginationCurrent={pageNum}
        columns={[...columns]}
        dataSource={tableData}
        gap={false}
        total={total}
        defaultPageSize={pageSize}
        onChange={(pagination, filters) => {
          if (filters?.appealStatus !== filterValue) {
            setFilterValue(filters?.appealStatus);

            fetchData({
              extraData: { appealStatuses: filters?.appealStatus },
            }).then();
          }
        }}
        getPopupContainer={() =>
          document.querySelector("billing-record-table") as HTMLElement
        }
        onRow={(row) => {
          const { billNo, id } = row;

          return {
            onClick: async (e) => {
              const type = getOperationType(e.target as HTMLDivElement);
              if (type === Operation.DownloadBill) {
                downloadRequest(
                  "/api/rendering/user/consume/bill/downloadUserMonthBill",
                  { billNo: billNo }
                ).then((res) => {
                  downloadFile(res as any);
                });
              }

              if (type === Operation.ConfirmBill) {
                setConfirmModal(id);
              }
              if (type === "appealBill") {
                setBillInfo(row);
                setAppealDrawer(true);
                setIsShowProgressForm(row.appealStatus !== null);
                row.appealStatus !== null && showComplaintProgressDialog(row);
              }
            },
          };
        }}
        onPageNumChange={async (num: number) => {
          setPageNum(num);
        }}
      />
      <PageStyle />

      <Modal
        title={t("consumption_confirm_bill")}
        open={!!openConfirmModal}
        onOk={async () => {
          await request.post(
            "/api/rendering/user/consume/bill/confirmUserMonthBills",
            { id: openConfirmModal }
          );
          setConfirmModal(undefined);
          await fetchData();
        }}
        onCancel={() => setConfirmModal(undefined)}
      >
        <p>{t("bill_confirm_text_1")}</p>
        <p>{t("bill_confirm_text_2")}</p>
        <p>{t("bill_confirm_text_3")}</p>
      </Modal>

      <Drawer
        width={480}
        open={appealDrawer}
        title={t("consumption_bill_appeal")}
        onClose={() => {
          closeAppealForm();
        }}
        drawerContent={
          <BillingAppealContainer>
            {isShowProgressForm ? (
              <AppealProgressForm
                billInfo={billInfo}
                appealForm={appealForm}
                appealProgressList={appealProgressList}
                currentStatus={Number(currentAppealStatus)}
              />
            ) : (
              <AppealForm
                appealForm={appealForm}
                billInfo={billInfo}
                isSubmitAppealForm={isSubmitAppealForm}
                onOpenProgress={() => {
                  fetchAppealData(billInfo?.billNo).then((res) => {
                    setBillInfo(res);
                    showComplaintProgressDialog(res);
                    setIsShowProgressForm(true);
                  });
                }}
              />
            )}
            <BillAppealOperateBtn>
              <Button type="default" onClick={() => closeAppealForm()}>
                {billInfo?.appealStatus !== undefined &&
                billInfo?.appealStatus !== null &&
                [
                  AppealStatus.Finished,
                  AppealStatus.Reject,
                  AppealStatus.Withdraw,
                ].includes(billInfo?.appealStatus)
                  ? t("property_dialog_okey")
                  : t("property_dialog_cancel")}
              </Button>
              {(billInfo?.appealStatus === null ||
                (billInfo?.appealStatus !== undefined &&
                  billInfo?.appealStatus !== null &&
                  [AppealStatus.Submitted, AppealStatus.Handling].includes(
                    Number(currentAppealStatus) || billInfo?.appealStatus
                  ))) && (
                <Button
                  type="primary"
                  className={
                    billInfo?.appealStatus !== null || isSubmitAppealForm
                      ? "submit-appeal"
                      : ""
                  }
                  onClick={() => handleOperateAppeal()}
                >
                  {billInfo?.appealStatus !== null || isSubmitAppealForm
                    ? t("consumption_withdrawal_complaint")
                    : t("consumption_bill_appeal_relaunch")}
                </Button>
              )}
            </BillAppealOperateBtn>
          </BillingAppealContainer>
        }
      />
      <Modal
        title={t("consumption_withdrawal_complaint")}
        open={!!openRevokeModal}
        onOk={async () => {
          const { code } = await request.post(
            "/api/rendering/user/consume/bill/quitAppealBills",
            { id: openRevokeModal }
          );
          if (code === 200) {
            message.success(t("message_success"));
          }
          if (code === 410) {
            message.error(t("consume_appeal_operate_error"));
          }
          setRevokeModal(undefined);
          closeAppealForm();
          await fetchData();
        }}
        onCancel={() => setRevokeModal(undefined)}
      >
        {billInfo?.confirmStatus === ConfirmStatus.Unconfirmed && (
          <p>{t("bill_withdrawal_complaint_1")}</p>
        )}
        <p>{t("bill_withdrawal_complaint_2")}</p>
      </Modal>
    </TableContainer>
  );
}

const BillingAppealContainer = styled.div`
  padding: 18px 28px 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  .appeal-form {
    .ant-form-item-label > label {
      height: 22px;
    }
    .ant-input {
      height: 40px;
    }
    .appeal-textarea {
      height: 120px;
    }
  }
  .submit-appeal {
    background: #db3227;
    border-color: #db3227;
  }
`;

const BillAppealOperateBtn = styled.div`
  display: flex;
  justify-content: center;
  button {
    width: 136px;
    height: 40px;
  }
  button:nth-child(2) {
    margin-left: 16px;
  }
`;

const PageStyle = createGlobalStyle`
  .ant-table-filter-dropdown .ant-dropdown-menu{
    background: #333336 !important;
  }
`;
